'use client';

import { Button, cn, Sheet, SheetContent, SheetDescription, SheetTitle } from '@shared/components/shadcn';
import { ChevronLeft } from '@shared/icons';
import { useIsMobile } from '@shared/utils';
import { useTranslations } from 'next-intl';
import type * as React from 'react';
import { useCallback } from 'react';

type PaymentsSheetProps = {
  children: React.ReactNode;
  title?: string;
  description?: string;
  className?: string;
  contentClassName?: string;
  showBackButton?: boolean;
  showHeader?: boolean;
  showTitle?: boolean;
  showCloseButton?: boolean;
  onBack?: () => void;
  footer?: React.ReactNode;
  desktopSide?: 'left' | 'right';
  mobileHeight?: string;
  desktopWidth?: string;
  'data-track-container'?: string;
  'data-track-feature'?: string;
  'data-testid'?: string;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  overlayStyle?: React.CSSProperties;
};

function PaymentsSheet({
  children,
  title = 'Payments',
  description,
  className,
  contentClassName,
  showBackButton = false,
  showHeader = true,
  showTitle = true,
  showCloseButton = true,
  onBack,
  footer,
  desktopSide = 'right',
  desktopWidth = 'max-w-md',
  'data-track-feature': dataTrackFeature,
  'data-testid': dataTestId,
  open = true,
  onOpenChange,
  overlayStyle,
  ...props
}: PaymentsSheetProps) {
  const isMobile = useIsMobile();
  const t = useTranslations('global_sheets');

  const getResponsiveClasses = () => {
    if (isMobile) {
      return cn('w-full max-w-none mx-0 p-0 max-h-[calc(100dvh-64px)]', contentClassName);
    }
    return cn('w-full mx-auto p-0 h-[100vh] rounded-none ', desktopWidth, contentClassName);
  };

  const getHeaderDataTrackContainer = useCallback(() => {
    switch (title) {
      case t('deposit.deposit_limits.title'):
        return 'Set Deposit Limit';
      case t('deposit.card_details.title'):
        return 'Method';
      case t('withdraw.bank_details.title'):
        return 'Account Details';
      default:
        return title;
    }
  }, [title, t]);

  return (
    <Sheet open={open} onOpenChange={onOpenChange} {...props}>
      {/* SheetTitle required for accessibility, but hidden as we don't need it */}
      <SheetTitle className="hidden" data-testid="payments-sheet-title">
        {title}
      </SheetTitle>
      {/* Hidden description for accessibility */}
      <SheetDescription className="sr-only" data-testid="payments-sheet-description">
        {description || title}
      </SheetDescription>
      <SheetContent
        side={isMobile ? 'bottom' : desktopSide}
        className={getResponsiveClasses()}
        closeIconSize="extra-small"
        showCloseButton={showCloseButton}
        onOpenAutoFocus={e => e.preventDefault()}
        customOverlayStyle={overlayStyle}
        data-testid={dataTestId}
        {...(dataTrackFeature && { 'data-track-feature': dataTrackFeature })}
        // TODO: Change to the actual type and count when we have the actual data
        data-track-type="FTD"
        data-track-count="1"
      >
        <div className={cn('flex flex-col h-full min-h-0', className)} data-testid="payments-sheet-content">
          {/* Header */}
          {showHeader && (title || showBackButton) && (
            <div
              className="flex items-center justify-between px-4 pb-4 pt-[calc(1rem+env(safe-area-inset-top,0px))] border-b border-border shrink-0 min-h-[60px]"
              data-track-container={getHeaderDataTrackContainer()}
            >
              {showBackButton ? (
                <div className="flex items-center">
                  <Button data-testid="payments-back-button" aria-label="Go back" variant="secondary" className="rounded-full p-2 h-8 w-8" onClick={onBack} data-track-name="Back">
                    <ChevronLeft size="large" />
                  </Button>
                </div>
              ) : (
                <div className="h-8 w-8" /> // Spacer when no back button
              )}
              {title && showTitle && (
                <div className="flex-1 flex items-center justify-center">
                  <SheetTitle className="paragraph-explanatory-strong" data-testid="payments-sheet-header-title">
                    {title}
                  </SheetTitle>
                </div>
              )}
              <div className="h-8 w-8" /> {/* Spacer for close button area */}
            </div>
          )}

          {/* Description */}
          {description && (
            <div className="px-4 py-2 border-b shrink-0">
              <p className="paragraph" data-testid="payments-sheet-description-visible">
                {description}
              </p>
            </div>
          )}

          {/* Content */}
          <div className="flex flex-col flex-1 overflow-y-auto">
            {/* eslint-disable-next-line @typescript-eslint/no-explicit-any */}
            {children as any}
            {/* Footer */}
            {/* eslint-disable-next-line @typescript-eslint/no-explicit-any */}
            {footer && <div className="p-4 mt-auto md:mt-0">{footer as any}</div>}
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}

export { PaymentsSheet };
